/**
 * Where the other person's voice comes out.
 *
 * iPhone: once the microphone is open, iOS puts a web app's audio session in
 * "play and record" mode without the "default to speaker" option native apps
 * get — so a plain <audio> element plays the call through the quiet top
 * earpiece. Held away from your ear, the call sounds silent (headphones work,
 * because they take over the route). Audio played through Web Audio
 * (AudioContext.destination) is routed as media and goes to the loudspeaker
 * even in that mode. So on iPhone the call plays through Web Audio by default
 * ("speaker"), and the <audio> element — earpiece — is the other choice.
 * Everywhere else the <audio> element already plays through the right device.
 */
import { getAudioContext } from "./tones.js";

export function isIosDevice(): boolean {
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

type AudioSessionType = "auto" | "playback" | "play-and-record" | "ambient" | "transient" | "transient-solo";

/**
 * iOS 17+: say plainly what the app is doing with audio instead of letting
 * Safari guess.
 *
 *  - "playback": ringing. Loud, loudspeaker, no microphone involved.
 *  - "play-and-record": an actual call or a voice recording. Quieter and
 *    earpiece-routed, which is why it must not be set while merely ringing.
 *  - "auto": nothing going on.
 */
export function setAudioSessionType(type: AudioSessionType): void {
  const session = (navigator as Navigator & { audioSession?: { type: AudioSessionType } }).audioSession;
  if (!session) return;
  try {
    session.type = type;
  } catch {
    /* unsupported value on this version */
  }
}

let source: MediaStreamAudioSourceNode | null = null;
let routedTrackId: string | null = null;

/**
 * Play the remote stream through Web Audio (→ loudspeaker on iPhone). Returns
 * false if that isn't possible, so the caller keeps the <audio> element.
 * Safe to call again when tracks change; it re-routes only if needed.
 */
export function routeToSpeaker(stream: MediaStream): boolean {
  const track = stream.getAudioTracks()[0];
  const ctx = getAudioContext();
  if (!track || !ctx) return false;
  if (source && routedTrackId === track.id) {
    void ctx.resume().catch(() => undefined);
    return true;
  }
  stopSpeakerRoute();
  try {
    // A stream holding just this track: the node always takes the stream's
    // first audio track at creation, so be explicit about which one.
    source = ctx.createMediaStreamSource(new MediaStream([track]));
    source.connect(ctx.destination);
    routedTrackId = track.id;
    void ctx.resume().catch(() => undefined);
    return true;
  } catch (err) {
    console.warn("couldn't route call audio through the speaker", err);
    stopSpeakerRoute();
    return false;
  }
}

export function stopSpeakerRoute(): void {
  try {
    source?.disconnect();
  } catch {
    /* already disconnected */
  }
  source = null;
  routedTrackId = null;
}

/** Is Web Audio actually producing sound right now? */
export function speakerRouteRunning(): boolean {
  return Boolean(source) && getAudioContext()?.state === "running";
}
